import React from "react"
import Layout from "../../components/layout"
import Hero from "../../components/hero"
import ReadNext from "../../components/read-next"
import Image from "../../components/image"
import { AppendixWaypoint, Appendix } from "../../components/appendix"
import { PageColumns, Column } from "../../components/page-column"
import { TabGroup } from "../../components/tab-group"
import BarChart from "../../components/bar-chart"
import StaticChart from "../../components/static-chart"
import MobileOnly from "../../components/mobile-only"
import List from "../../components/list"
import Accordion from "../../components/accordion"
import { Stat } from "../../components/stat"
import IconHand from "../../images/what-we-do/capacity-development/hand.inline.svg"
import IconHands from "../../images/what-we-do/capacity-development/hands.inline.svg"
import IconDiploma from "../../images/what-we-do/capacity-development/diploma.inline.svg"
import IconOfficials from "../../images/what-we-do/capacity-development/officials.inline.svg"
import IconUmbrella from "../../images/what-we-do/capacity-development/umbrella.inline.svg"
import IconLanguages from "../../images/what-we-do/capacity-development/languages.inline.svg"
import CDSpending from "../../images/what-we-do/capacity-development/cd_spending.inline.svg"
import Table from "../../components/table"
import CourseParticipationData from "../../data/tables/course-participation.json"
import SpendingOnCDData from "../../data/tables/spending-on-cd.json"

const PAGESLUG = "capacity-development"

export default function CapacityDevelopment() {
  return (
    <Layout>
      <section className="page page--pullBottom capacity-development page--margin-large">
        <Hero
          title="Capacity Development"
          image="what-we-do/capacity-development/hero.jpg"
        />

        <div className="page__intro container md:container--small text-block">
          <p className="dropcap">
            <b>Strengthening the capacity of institutions—</b>including central
            banks, finance ministries, revenue administrations, statistical
            agencies, and financial sector supervisory agencies—results in more
            effective policies and greater economic stability and inclusion. The
            IMF works with countries to modernize their economic policies and
            strengthen these institutions by providing technical assistance and
            training focused on issues that are critical to economic stability
            and growth.
          </p>
        </div>

        <PageColumns modifiers="text-block">
          <Column type="media">
            <Image
              path="what-we-do/capacity-development/overview.jpg"
              alt="Man speaking to others"
            />

            <div style={{ marginTop: 200 }}>
              <StaticChart
                figureNumber="2.1"
                title="Capacity Development Spending as a Share of Major IMF Activities"
                subTitle="FY 2021"
                ariaLabel="Pie chart showing Capacity Development spending as a share of major IMF activities"
                classes="chart--cd-spending"
                scaling={false}
                paddingbottom="100%"
              >
                <CDSpending />
              </StaticChart>
            </div>

            <div style={{ marginTop: 200 }}>
              <BarChart
                id="spending-on-capacity-development"
                figureNumber="2.2"
                title="Spending on Capacity Development"
                subTitle="FY 2017–21 (millions of US dollars)"
                source="Sources: IMF Office of Budget and Planning Analytic Costing and Estimation System; IMF Office of Budget and Planning; and IMF staff calculations."
              >
                <Table
                  data={SpendingOnCDData}
                  figureNumber="2.2"
                  title="Spending on Capacity Development"
                  subTitle="FY 2017–21 (millions of US dollars)"
                  source="Sources: IMF Office of Budget and Planning Analytic Costing and Estimation System; IMF Office of Budget and Planning; and IMF staff calculations."
                />
              </BarChart>
            </div>
          </Column>

          <Column type="text">
            <h2>Overview</h2>
            <AppendixWaypoint page={PAGESLUG} id={1} />
            <p>
              The IMF provides capacity development—hands-on technical
              assistance, policy-oriented training, and peer-learning
              opportunities—to help countries build sustainable and resilient
              institutions. These efforts are an important contribution to
              countries’ progress toward the UN Sustainable Development Goals.
              The IMF has a number of development partners that support more
              than half its capacity development work, including to help fund
              the first phase of the IMF’s COVID-19 CD Initiative to build
              forward better and work toward a greener, smarter, and more
              equitable future.
            </p>
            <MobileOnly>
              <Image
                path="what-we-do/capacity-development/overview.jpg"
                alt="Man speaking to others"
              />
            </MobileOnly>
            <p>
              Capacity development focuses on the IMF’s core areas of expertise,
              such as public finances, financial sector stability, and economic
              statistics. The IMF also provides CD in cross-cutting issues, such
              as income inequality, gender equality, corruption, and climate
              change, within its areas of expertise. The IMF is uniquely
              positioned to support its membership with its global reach,
              institutional experience, and world-class expertise. The IMF’s
              capacity development activities benefit all members, but support
              is particularly targeted and tailored to fragile states.
            </p>
            <AppendixWaypoint page={PAGESLUG} id={2} />
            <p>
              At the request of country authorities, IMF country teams and
              technical experts develop and implement an integrated work plan
              tailored to countries’ specific needs. The IMF works with
              countries through a global network of 17 regional centers; the
              newest center, the Caucasus, Central Asia, and Mongolia Regional
              Capacity Development Center (CCAMTAC), opened virtually in
              February 2021. In response to the COVID-19 pandemic, the IMF has
              provided real-time policy advice and capacity development to more
              than 175 countries.
            </p>
            <MobileOnly>
              <StaticChart
                figureNumber="2.1"
                title="Capacity Development Spending as a Share of Major IMF Activities"
                subTitle="FY 2021"
                ariaLabel="Bar chart showing IMF Financial Support from 2002 to 2020"
                classes="chart--cd-spending"
                scaling={true}
                paddingBottom={10}
              >
                <CDSpending />
              </StaticChart>
            </MobileOnly>
            <p>
              The IMF published more than 80 technical notes on crisis-related
              policy issues during the early stages of the COVID-19 crisis. It
              also helped countries strengthen their collection and publication
              of economic data to improve economic decision-making and boost
              transparency. As countries ramped up emergency spending, the IMF
              helped strengthen their governance frameworks to better ensure
              that money quickly reached those most in need and that governments
              were accountable for how they spent the money.
            </p>
            <p>
              The IMF quickly adapted its capacity development delivery tools to
              the new remote environment imposed by the pandemic. It has
              continued to help its members address new challenges such as
              finding digital solutions for direct cash transfers as well as for
              tax compliance and revenue collection, protecting health
              expenditures and other social spending, and preparing for the
              challenges posed by climate change. Participation in the IMF’s
              free online courses has increased sharply.
            </p>
          </Column>
        </PageColumns>

        <PageColumns modifiers="text-block">
          <Column type="text">
            <h2>The IMF Online Learning Program: A Global Public Good</h2>
            <AppendixWaypoint page={PAGESLUG} id={3} />
            <p>
              The IMF’s online learning program has played an important role in
              serving the needs of our membership during the COVID-19 crisis.
            </p>
            <p>
              Over the past year, participation in the program has increased
              significantly, and more than 7,000 government officials have
              successfully completed an IMF course. In April 2020, the{" "}
              <a href="https://www.youtube.com/channel/UCrSr-v_sSH6MXBopw5VUAUQ">
                IMF Institute Learning Channel
              </a>{" "}
              was launched on YouTube, offering bite-sized microlearning videos
              in various areas of IMF expertise. Since the channel’s inception,
              this new and agile modality of learning has attracted more than
              4,400 subscribers and 233,000 individual views.
            </p>
            <MobileOnly>
              <BarChart
                id="course-participation"
                title="Online Learning Course Participation Over Time"
              >
                <Table
                  data={CourseParticipationData}
                  title="Online Learning Course Participation Over Time"
                />
              </BarChart>
            </MobileOnly>
            <p>
              The online curriculum continues to be enriched in key areas of
              global interest. A new series of five courses on public debt
              analysis, sustainability, and management launched in 2020,
              including a course on the Joint World Bank–IMF Debt Sustainability
              Framework for Low-Income Countries{" "}
              <a href="https://www.imf.org/external/np/ins/english/training.htm">
                LIC DSFx
              </a>
              , developed with the World Bank. New courses on macroeconomic
              statistics (<a href="https://youtu.be/N3XnRJWsJk4">GFSx</a>,{" "}
              <a href="https://youtu.be/xXNUyPIJRhE">BOPx</a>) are now
              available, together with six new translations of online courses.
              An inclusive growth course (
              <a href="https://youtu.be/5N3O8RXn1nI">IGx</a>), together with
              courses on revenue forecasting (
              <a href="https://www.youtube.com/watch?v=jo4vOzEsjaM&list=PLrRVF2zCyHxvG1oQvMuH14Wvtoqr2omwO&index=2">
                RFAx
              </a>
              ) and tax administration (
              <a href="https://www.imf.org/en/videos/view?vid=6260381925001">
                VITARA
              </a>
              ) were launched from April to June 2021.
            </p>
            <MobileOnly>
              <BarChart
                id="spending-on-capacity-development"
                figureNumber="2.2"
                title="Spending on Capacity Development"
                subTitle="FY 2017–21 (millions of US dollars)"
                source="Sources: IMF Office of Budget and Planning Analytic Costing and Estimation System; IMF Office of Budget and Planning; and IMF staff calculations."
              >
                <Table
                  data={SpendingOnCDData}
                  figureNumber="2.2"
                  title="Spending on Capacity Development"
                  subTitle="FY 2017–21 (millions of US dollars)"
                  source="Sources: IMF Office of Budget and Planning Analytic Costing and Estimation System; IMF Office of Budget and Planning; and IMF staff calculations."
                />
              </BarChart>
            </MobileOnly>
            <p>
              <i>
                All courses on the edX platform—used for the IMF’s online
                learning program—are available anytime, anywhere, and at no
                cost, which makes the program a global public good, spearheading
                knowledge and skills for a more sustainable and inclusive global
                economy.
              </i>
            </p>
          </Column>

          <Column type="media">
            <BarChart
              id="course-participation"
              title="Online Learning Course Participation Over Time"
            >
              <Table
                data={CourseParticipationData}
                title="Online Learning Course Participation Over Time"
              />
            </BarChart>
          </Column>
        </PageColumns>

        <div className="container container--full md:container--xl lg:container--large md:container--xl lg:container--large">
          <ul className="columns columns--three">
            <li>
              <div className="stat-mega">
                <div className="stat-mega__value">
                  <IconHand />
                  <Stat number={251} />
                  <div className="stat-mega__unit">M</div>
                </div>
                <p className="title title--s">
                  for <b>hands-on technical advice</b>, policy-oriented
                  training, and peer learning
                </p>
              </div>
            </li>
            <li>
              <div className="stat-mega">
                <div className="stat-mega__value">
                  <IconHands />
                  <Stat number={3446} />
                </div>
                <p className="title title--s">
                  <b>technical assistance</b> visits involving 1,275 experts
                </p>
              </div>
            </li>
            <li>
              <div className="stat-mega">
                <div className="stat-mega__value">
                  <IconUmbrella />
                  <Stat number={4} />
                </div>
                <p className="title title--s">
                  <b>fragile states</b> among the top 10 recipients of technical
                  assistance
                </p>
              </div>
            </li>
          </ul>
          <ul className="columns columns--three">
            <li>
              <div className="stat-mega">
                <div className="stat-mega__value">
                  <IconDiploma />
                  <Stat number={360} />
                </div>
                <p className="title title--s">
                  <b>courses delivered</b>
                </p>
              </div>
            </li>
            <li>
              <div className="stat-mega">
                <div className="stat-mega__value">
                  <IconLanguages />
                  <Stat number={8} />
                </div>
                <p className="title title--s">
                  <b>training languages offered</b>
                </p>
              </div>
            </li>
            <li>
              <div className="stat-mega">
                <div className="stat-mega__value">
                  <IconOfficials />
                  <Stat number={14926} />
                </div>
                <p className="title title--s">
                  <b>officials trained</b>
                </p>
              </div>
            </li>
          </ul>
        </div>

        <div className="container container--full md:container--xl lg:container--large">
          <ul className="columns columns--three">
            <li>
              <List id="cd-partners-for-imf" listType="ol" />
            </li>
            <li>
              <List id="cd-recipients-spending" listType="ol" />
            </li>
            <li>
              <List id="cd-recipients-participation" listType="ol" />
            </li>
          </ul>
        </div>

        <div className="container container--full md:container--xl lg:container--large">
          <TabGroup
            controls={["Region", "Income Group", "Topic"]}
            title="Direct Capacity Development Delivery"
            subtitle="FY 2017–21 (percent share of total, by fiscal year)"
          >
            <ul className="swipe-view swipe-view--columns--five">
              <li>
                <BarChart
                  id="capacity-africa"
                  subTitle="Sub-Saharan Africa"
                  measure="percent"
                />
              </li>
              <li>
                <BarChart
                  id="capacity-asia"
                  subTitle="Asia and Pacific"
                  measure="percent"
                />
              </li>
              <li>
                <BarChart
                  id="capacity-europe"
                  subTitle="Europe"
                  measure="percent"
                />
              </li>
              <li>
                <BarChart
                  id="capacity-middle-east"
                  subTitle="Middle East and Central Asia"
                  measure="percent"
                />
              </li>
              <li>
                <BarChart
                  id="capacity-western-hemisphere"
                  subTitle="Western Hemisphere"
                  measure="percent"
                />
              </li>
            </ul>

            <ul className="swipe-view swipe-view--columns--three">
              <li>
                <BarChart
                  id="capacity-income-advanced"
                  subTitle="Advanced Economies"
                  measure="percent"
                />
              </li>
              <li>
                <BarChart
                  id="capacity-income-emerging"
                  subTitle="Emerging Market and Middle-Income Economies"
                  measure="percent"
                />
              </li>
              <li>
                <BarChart
                  id="capacity-income-low"
                  subTitle="Low-Income Developing Countries"
                  measure="percent"
                />
              </li>
            </ul>

            <ul className="swipe-view swipe-view--columns--five">
              <li>
                <BarChart
                  id="capacity-topic-public"
                  subTitle="Public Finances"
                  measure="percent"
                />
              </li>
              <li>
                <BarChart
                  id="capacity-topic-monetary"
                  subTitle="Monetary and Financial Systems"
                  measure="percent"
                />
              </li>
              <li>
                <BarChart
                  id="capacity-topic-statistics"
                  subTitle="Statistics"
                  measure="percent"
                />
              </li>
              <li>
                <BarChart
                  id="capacity-topic-legal"
                  subTitle="Legal Frameworks"
                  measure="percent"
                />
              </li>
              <li>
                <BarChart
                  id="capacity-topic-macroeconomic"
                  subTitle="Macroeconomic Frameworks"
                  measure="percent"
                />
              </li>
            </ul>
          </TabGroup>
        </div>

        <div className="container container--full md:container--xl lg:container--large">
          <TabGroup
            controls={["Region", "Income Group"]}
            title="Training Participation"
            subtitle="FY 2017–21 (number of participants)"
          >
            <ul className="swipe-view swipe-view--columns--five">
              <li>
                <BarChart
                  id="training-africa"
                  subTitle="Sub-Saharan Africa"
                  measure="participants"
                />
              </li>
              <li>
                <BarChart
                  id="training-asia"
                  subTitle="Asia and Pacific"
                  measure="participants"
                />
              </li>
              <li>
                <BarChart
                  id="training-europe"
                  subTitle="Europe"
                  measure="participants"
                />
              </li>
              <li>
                <BarChart
                  id="training-middle-east"
                  subTitle="Middle East and Central Asia"
                  measure="participants"
                />
              </li>
              <li>
                <BarChart
                  id="training-western-hemisphere"
                  subTitle="Western Hemisphere"
                  measure="participants"
                />
              </li>
            </ul>

            <ul className="swipe-view swipe-view--columns--three">
              <li>
                <BarChart
                  id="training-advanced"
                  subTitle="Advanced Economies"
                  measure="participants"
                />
              </li>
              <li>
                <BarChart
                  id="training-emerging"
                  subTitle="Emerging Market and Middle-Income Economies"
                  measure="participants"
                />
              </li>
              <li>
                <BarChart
                  id="training-low-income"
                  subTitle="Low-Income Developing Countries"
                  measure="participants"
                />
              </li>
            </ul>
          </TabGroup>
        </div>

        <div className="container container--full md:container--xl lg:container--large">
          <Accordion id="thematic-country-funds" />
        </div>

        <div className="container container--full md:container--xl lg:container--large">
          <Accordion id="regional-capacity-development-centers" />
        </div>
      </section>

      <ReadNext sectionID="who-we-are" />
      <Appendix page={PAGESLUG} />
    </Layout>
  )
}
